/*
 * GameHeader.tsx
 * Displays game metadata including game number, date, and title.
 */
'use client';

import React, { useCallback, useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { easeOutQuad } from '@/core/easing';
import { decryptCoverStoryAlbum, formatDate } from '@/core/utils';

// Styles
import classNames from 'classnames/bind';
import styles from './GameHeader.module.scss';
const cx = classNames.bind(styles);

// --------------------

interface GameHeaderProps {
  title?: string;
  gameData?: CoverStoryGameData;
  gameState?: 'playing' | 'won' | 'lost';
}

const GameHeader: React.FC<GameHeaderProps> = ({ title, gameData, gameState = 'playing' }) => {

  const gameNumber = gameData?.gameNumber;

  const [albumData, setAlbumData] = React.useState<{ album: string; artist: string } | null>(null);

  // Format date
  const displayDate = formatDate(gameData.coverStoryDate, 'EEE MMM d, yyyy');

  // Construct pill text
  const pillText = `#${gameNumber} - ${displayDate}`;

  // -------------------- Headline values

  // Game title (clue)
  const textValue_gameName = title;

  // Game answer
  function getGameAnswerText(
    gameState: 'playing' | 'won' | 'lost',
  ): string {
    const isOver = gameState === 'won' || gameState === 'lost';
    if (!isOver) return '';
    return `${albumData?.artist} - ${albumData?.album}`;
  }
  const textValue_gameAnswer = useMemo(() => getGameAnswerText(gameState), [gameState, albumData]);

  // Current headline to display
  const currentHeadline = gameState === 'playing' ? textValue_gameName : textValue_gameAnswer;

  const decryptAlbumData = useCallback(async () => {
    const decrypted = await decryptCoverStoryAlbum(gameData.albumFormatted);
    setAlbumData(decrypted);
  }, [gameData]);

  useEffect(() => {
    decryptAlbumData();
  }, [gameData]);

  // -------------------- Render

  return (
    <div className={cx('game-header')}>

      {/* Pill */}
      <div className={cx('tag-l', 'metadata-pill')}>
        {pillText}
      </div>

      {/* Title with AnimatePresence for smooth transitions */}
      <h2 className={cx('title-uc-xs', 'headline')}>
        <AnimatePresence mode="wait">
          <motion.span
            key={gameState} // Change key when gameState changes to trigger exit/enter
            initial={{ opacity: 0, y: '-100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ duration: 0.4, ease: easeOutQuad }}
            style={{ display: 'block' }}
          >
            {currentHeadline}
          </motion.span>
        </AnimatePresence>
      </h2>

    </div>
  );

};

export default GameHeader;
